import { CrmTaskStatus } from '@plato/data/prisma';
import { endOfDay } from 'date-fns';
import { BarChart3, Calendar } from 'lucide-react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import DraftIcon from '../../../assets/icons/draft-icon.tsx';
import { NavChart } from '../../../assets/icons/nav-chart.tsx';
import { NavCustomer } from '../../../assets/icons/nav-customer.tsx';
import { NavHome } from '../../../assets/icons/nav-home.tsx';
import { NavTask } from '../../../assets/icons/nav-task.tsx';
import { OrderIcon } from '../../../assets/icons/order-icon.tsx';
import { QuoteIcon } from '../../../assets/icons/quote-icon.tsx';
import TransactionIcon from '../../../assets/icons/transaction-icon.tsx';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmTaskFilterOptions } from '../../../server/dto/crm-task-dto.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import type { MenuItem, MenuSubItem } from '../contracts.ts';
import { NavPathOverrideEnum } from '../contracts.ts';

type RawMenuItem = Omit<MenuItem, 'subItems'> & {
    subItems?: (MenuSubItem | undefined | false)[];
};

export const usePlatoLayoutNavigation = () => {
    const { platoUser } = useAppState();
    const { t } = useLocale();
    const location = useLocation();

    const {
        canReadOrders,
        canReadQuotes,
        transactionDraftAvailability,
        enableCustomerEventPlanning,
        enableVisitAnalytics,
    } = useAppConfig();

    const { data: taskCount } = api.platoUser.query.tasks.getManyTasks.count.useQuery(
        {
            customerIds: [],
            assignedToIds: [platoUser?.id || ''],
            assignedByIds: [],
            status: CrmTaskStatus.OPEN,
            dueDate: [
                {
                    direction: 'LT',
                    timestamp: endOfDay(new Date()).toISOString(),
                },
            ],
        } satisfies CrmTaskFilterOptions,
        {
            enabled: !!platoUser?.id,
            refetchInterval: 60 * 1000,
        },
    );

    const navigation: MenuItem[] = useMemo(() => {
        if (!platoUser) {
            return [];
        }

        const items: (RawMenuItem | undefined | false)[] = [
            {
                id: NavPathOverrideEnum.HOME,
                name: t('page_title__home'),
                href: '/home',
                icon: NavHome,
            },
            {
                id: NavPathOverrideEnum.CUSTOMERS,
                name: t('page_title__customers'),
                href: '/customers',
                icon: NavCustomer,
            },
            {
                id: NavPathOverrideEnum.TRANSACTIONS,
                name: t('page_title__transactions'),
                icon: TransactionIcon,
                subItems: [
                    canReadQuotes && {
                        id: NavPathOverrideEnum.SALES_QUOTES,
                        name: t('page_title__sales_quotes'),
                        href: '/quotes',
                        icon: QuoteIcon,
                    },
                    canReadOrders && {
                        id: NavPathOverrideEnum.SALES_ORDERS,
                        name: t('page_title__sales_orders'),
                        href: '/orders',
                        icon: OrderIcon,
                    },
                    transactionDraftAvailability !== 'UNAVAILABLE' && {
                        id: NavPathOverrideEnum.SALES_DRAFTS,
                        name: t('page_title__sales_drafts'),
                        href: '/drafts',
                        icon: DraftIcon,
                    },
                ],
            },
            {
                id: NavPathOverrideEnum.EXPLORER,
                name: t('page_title__explorer'),
                href: '/explorer',
                icon: NavChart,
            },
            {
                id: NavPathOverrideEnum.TASKS,
                name: t('page_title__tasks'),
                href: '/tasks',
                icon: NavTask,
                count: taskCount,
            },
            enableCustomerEventPlanning && {
                id: NavPathOverrideEnum.EVENTS,
                name: t('page_title__events'),
                href: '/events',
                icon: Calendar,
            },
            enableVisitAnalytics && {
                id: NavPathOverrideEnum.VISIT_ANALYTICS,
                name: t('page_title', { ns: 'visit-analytics' }),
                href: '/visit-analytics',
                icon: BarChart3,
            },
        ];

        return items
            .map((item): MenuItem | undefined => {
                if (!item) {
                    return undefined;
                }

                if (!item.subItems) {
                    return item as MenuItem;
                }

                const subItems = item.subItems.filter((i): i is MenuSubItem => Boolean(i));

                if (subItems.length === 1) {
                    return {
                        ...subItems[0]!,
                        count: item.count,
                    };
                }

                if (subItems.length === 0) {
                    return undefined;
                }

                return {
                    ...item,
                    subItems: subItems.length > 1 ? subItems : undefined,
                };
            })
            .filter((i): i is MenuItem => Boolean(i));
    }, [
        platoUser,
        t,
        canReadQuotes,
        canReadOrders,
        transactionDraftAvailability,
        taskCount,
        enableCustomerEventPlanning,
        enableVisitAnalytics,
    ]);

    const activeSection = useMemo(() => {
        // Special case: /integrations/tapi routes should highlight Customers
        if (location.pathname.startsWith('/integrations/tapi')) {
            return NavPathOverrideEnum.CUSTOMERS;
        }

        for (const item of navigation) {
            if (item.subItems) {
                for (const subItem of item.subItems) {
                    if (location.pathname.startsWith(subItem.href)) {
                        return subItem.id;
                    }
                }
            }
        }

        for (const item of navigation) {
            if (item.href && location.pathname.startsWith(item.href)) {
                return item.id;
            }
        }

        return NavPathOverrideEnum.HOME;
    }, [navigation, location.pathname]);

    return { navigation, activeSection };
};
